import { getCallAdminLogDetailQuery } from "@domain/call_logs"
import { customersGetUsers } from "@domain/customer/infos"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DownloadIcon from "@mui/icons-material/Download"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  buildDebugBundle,
  copyDebugBundle,
  downloadDebugBundle
} from "@utils/debug_bundle"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { CallAdminLogStep } from "__generated__/graphql"
import { useEffect, useMemo, useState } from "react"
import CallRunTimeline from "./CallRunTimeline"
import { collectAgentNamesFromLogs } from "./callRunTimeline.utils"
import LogGroup from "./LogGroup"

interface Props {
  open: boolean
  onClose: () => void
  callSid: string | null
}

type StepGroup = [string, CallAdminLogStep[]]

function normalizeValue(value?: string | null) {
  return (value || "").toLowerCase()
}

function matchesStepFilters(
  log: CallAdminLogStep,
  groupKey: string,
  searchTerm: string,
  dataSearchTerm: string
) {
  const normalizedSearch = normalizeValue(searchTerm)
  const normalizedDataSearch = normalizeValue(dataSearchTerm)

  const matchesGeneral =
    !normalizedSearch ||
    normalizeValue(log.stepName).includes(normalizedSearch) ||
    normalizeValue(log.type).includes(normalizedSearch) ||
    normalizeValue(log.status).includes(normalizedSearch) ||
    normalizeValue(groupKey).includes(normalizedSearch)

  const strData = log.data ? JSON.stringify(log.data).toLowerCase() : ""
  const strError = log.error ? JSON.stringify(log.error).toLowerCase() : ""

  const matchesData =
    !normalizedDataSearch ||
    strData.includes(normalizedDataSearch) ||
    strError.includes(normalizedDataSearch)

  return matchesGeneral && matchesData
}

function getSdkFlowId(log: CallAdminLogStep) {
  if (!log.data || typeof log.data !== "object" || Array.isArray(log.data)) {
    return log.correlationId || "sdk-unknown"
  }

  const payload = log.data as Record<string, unknown>
  const flowId =
    payload.flowId ||
    payload.twilioFlowId ||
    payload.parentCallSid ||
    payload.callSid ||
    log.correlationId

  return typeof flowId === "string" && flowId.trim().length > 0
    ? flowId
    : "sdk-unknown"
}

export default function CallAdminLogVisualizerDrawer({
  open,
  onClose,
  callSid
}: Props) {
  const [selectedStep, setSelectedStep] = useState<CallAdminLogStep | null>(
    null
  )
  const [searchTerm, setSearchTerm] = useState("")
  const [dataSearchTerm, setDataSearchTerm] = useState("")

  useEffect(() => {
    if (open) {
      setSearchTerm("")
      setDataSearchTerm("")
      setSelectedStep(null)
    }
  }, [open, callSid])

  const { data: callLog, isLoading } = useQuery({
    queryKey: ["call-log-detail", callSid],
    queryFn: async () => {
      if (!callSid) return null
      const response = await getCallAdminLogDetailQuery(callSid)
      if (!response.isSuccess || !response.data) {
        return null
      }
      return response.data
    },
    enabled: !!callSid && open
  })

  // The audit steps identify agents by cPerson id. Most names are already inside the log
  // (the agent-selection steps carry them), so the roster is only fetched to fill the gaps.
  const customerId = callLog?.customer ? String(callLog.customer) : ""
  const { data: customerPeople } = useQuery({
    queryKey: ["call-log-agents", customerId],
    queryFn: async () => {
      const response = await customersGetUsers({
        customerId,
        page: 0,
        pageSize: 500
      })
      return response.data?.docs ?? []
    },
    enabled: open && !!customerId,
    staleTime: 5 * 60 * 1000
  })

  const agentNames = useMemo(() => {
    const names = collectAgentNamesFromLogs(callLog)
    ;(customerPeople ?? []).forEach((person) => {
      const id = person?._id ? String(person._id) : ""
      const fullName =
        `${person?.firstName ?? ""} ${person?.lastName ?? ""}`.trim()
      if (id && fullName) names.set(id, fullName)
    })
    return names
  }, [callLog, customerPeople])

  const backendGroups = useMemo(() => {
    if (!callLog?.logs) return []

    const groupedMap = new Map<string, CallAdminLogStep[]>()

    callLog.logs.forEach((log) => {
      if (normalizeValue(log.type) === "sdk") {
        return
      }

      const cid = log.correlationId || "unknown"
      if (!groupedMap.has(cid)) {
        groupedMap.set(cid, [])
      }
      groupedMap.get(cid)?.push(log)
    })

    const sortedGroups = Array.from(groupedMap.entries()).sort(
      ([, stepsA], [, stepsB]) => {
        if (!stepsA || !stepsB) return 0
        const startA =
          stepsA.find((s) => s.type === "webhook" && s.status === "started")
            ?.timestamp || 0
        const startB =
          stepsB.find((s) => s.type === "webhook" && s.status === "started")
            ?.timestamp || 0
        return new Date(startA).getTime() - new Date(startB).getTime()
      }
    )

    if (!searchTerm && !dataSearchTerm) {
      return sortedGroups
    }

    const filteredGroups: StepGroup[] = []

    sortedGroups.forEach(([cid, steps]) => {
      const filteredSteps = steps.filter((log) =>
        matchesStepFilters(log, cid, searchTerm, dataSearchTerm)
      )

      if (filteredSteps.length > 0) {
        filteredGroups.push([cid, filteredSteps])
      }
    })

    return filteredGroups
  }, [callLog, searchTerm, dataSearchTerm])

  const sdkGroups = useMemo(() => {
    if (!callLog?.logs) return []

    const groupedMap = new Map<string, CallAdminLogStep[]>()

    callLog.logs
      .filter((log) => normalizeValue(log.type) === "sdk")
      .sort((a, b) => a.timestamp - b.timestamp)
      .forEach((log) => {
        const flowId = getSdkFlowId(log)
        const current = groupedMap.get(flowId) || []
        groupedMap.set(flowId, [...current, log])
      })

    const allGroups = Array.from(groupedMap.entries()) as StepGroup[]

    if (!searchTerm && !dataSearchTerm) {
      return allGroups
    }

    return allGroups
      .map(([flowId, steps]) => [
        flowId,
        steps.filter((log) =>
          matchesStepFilters(log, flowId, searchTerm, dataSearchTerm)
        )
      ])
      .filter(([, steps]) => steps.length > 0) as StepGroup[]
  }, [callLog, searchTerm, dataSearchTerm])

  const rawSdkLogs = useMemo(
    () =>
      (callLog?.logs || [])
        .filter((log) => normalizeValue(log.type) === "sdk")
        .sort((a, b) => a.timestamp - b.timestamp),
    [callLog]
  )

  useEffect(() => {
    if (searchTerm || dataSearchTerm) {
      const combinedGroups = [...backendGroups, ...sdkGroups]

      if (combinedGroups.length > 0) {
        const firstGroup = combinedGroups[0]
        if (firstGroup && firstGroup[1] && firstGroup[1].length > 0) {
          setSelectedStep(firstGroup[1][0])
        }
      } else {
        setSelectedStep(null)
      }
    }
  }, [backendGroups, sdkGroups, searchTerm, dataSearchTerm])

  const handleStepClick = (step: CallAdminLogStep) => {
    setSelectedStep(step)
  }

  /**
   * The raw document plus the two things a local reader cannot re-derive from it: the
   * cPerson -> name roster (fetched from a separate query here) and which environment
   * produced it. Nothing is filtered or reshaped — the skill needs ground truth, and the
   * drawer's own search/derived views must not leak into the export.
   */
  const bundle = useMemo(() => {
    if (!callLog) return null
    return buildDebugBundle({
      kind: "call",
      id: callLog.callSid || callSid || "unknown",
      data: {
        callLog,
        agentNames: Object.fromEntries(agentNames)
      }
    })
  }, [callLog, callSid, agentNames])

  const handleCopyBundle = async () => {
    if (!bundle) {
      showErrorToast("Kopyalanacak call bulunamadı")
      return
    }
    await copyDebugBundle(bundle)
  }

  const handleDownloadBundle = () => {
    if (!bundle) {
      showErrorToast("İndirilecek call bulunamadı")
      return
    }
    downloadDebugBundle(bundle)
  }

  const handleCopySdkLogs = async () => {
    if (rawSdkLogs.length === 0) {
      showErrorToast("Kopyalanacak SDK logu bulunamadi")
      return
    }

    const payload = {
      callSid: callLog?.callSid || null,
      parentCallSid: callLog?.parentCallSid || null,
      childCallSids: callLog?.childCallSids || [],
      totalSdkLogCount: rawSdkLogs.length,
      sdkLogs: rawSdkLogs
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
      showSuccessToast("Tum SDK loglari JSON olarak kopyalandi")
    } catch {
      showErrorToast("SDK loglari kopyalanamadi")
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: "70vw" }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          borderBottom={1}
          borderColor="divider"
        >
          <Typography variant="h6">Call Flow Visualizer</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Debug bundle'ı JSON olarak kopyala (/call-debug için)">
              <IconButton
                size="small"
                onClick={handleCopyBundle}
                disabled={!callLog}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Debug bundle'ı .json dosyası olarak indir">
              <IconButton
                size="small"
                onClick={handleDownloadBundle}
                disabled={!callLog}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading && <LinearProgress />}

        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#f1f5f9",
              borderRight: 1,
              borderColor: "divider"
            }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.paper"
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search step, type, status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                size="small"
                fullWidth
                placeholder="Search inside code data..."
                value={dataSearchTerm}
                onChange={(e) => setDataSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
              <CallRunTimeline log={callLog} agentNames={agentNames} />

              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Call Mapping
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Parent Call SID: {callLog?.parentCallSid || "-"}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Current Call SID: {callLog?.callSid || "-"}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Child Call SIDs:{" "}
                  {callLog?.childCallSids?.length
                    ? callLog.childCallSids.join(", ")
                    : "-"}
                </Typography>
                {/* Kept out of the list on purpose — only useful once you are already
                    debugging one call. TEAM calls have no phone platform behind them. */}
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Platform ID: {callLog?.platformId || "-"}
                </Typography>
              </Box>

              {backendGroups.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                    Backend Flow Logs
                  </Typography>
                  {backendGroups.map(([correlationId, steps]) => (
                    <LogGroup
                      key={`backend-${correlationId}`}
                      correlationId={correlationId}
                      steps={steps}
                      onStepClick={handleStepClick}
                    />
                  ))}
                </Box>
              )}

              {sdkGroups.length > 0 && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Twilio SDK Logs
                    </Typography>
                    <Tooltip title="Tum SDK loglarini JSON olarak kopyala">
                      <IconButton size="small" onClick={handleCopySdkLogs}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {sdkGroups.map(([flowId, steps]) => (
                    <SdkFlowGroup
                      key={`sdk-${flowId}`}
                      flowId={flowId}
                      steps={steps}
                      onStepClick={handleStepClick}
                    />
                  ))}
                </Box>
              )}

              {!isLoading &&
                backendGroups.length === 0 &&
                sdkGroups.length === 0 && (
                  <Box p={4} textAlign="center">
                    <Typography color="text.secondary">
                      No logs found for this call.
                    </Typography>
                  </Box>
                )}
            </Box>
          </Box>

          <Box
            sx={{
              width: 650,
              minWidth: 350,
              display: "flex",
              flexDirection: "column",
              borderLeft: 1,
              borderColor: "divider",
              bgcolor: "background.paper"
            }}
          >
            {selectedStep ? (
              <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Step Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <DetailRow label="Name" value={selectedStep.stepName} />
                <DetailRow label="Type" value={selectedStep.type} />
                <DetailRow label="Status" value={selectedStep.status} />
                <DetailRow
                  label="Time"
                  value={new Date(selectedStep.timestamp).toLocaleString()}
                />
                <DetailRow
                  label="Duration"
                  value={
                    selectedStep.duration ? `${selectedStep.duration}ms` : "-"
                  }
                />

                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Data (JSON)
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#1e293b",
                      color: "#e2e8f0",
                      p: 2,
                      borderRadius: 1,
                      overflowX: "auto",
                      fontSize: "0.80rem",
                      fontFamily: "monospace"
                    }}
                  >
                    <HighlightedJSON
                      data={selectedStep.data}
                      searchTerm={dataSearchTerm}
                    />
                  </Box>
                </Box>

                {selectedStep.error && (
                  <Box mt={3}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Error
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#fee2e2",
                        color: "#991b1b",
                        p: 2,
                        borderRadius: 1,
                        fontSize: "0.85rem",
                        fontFamily: "monospace"
                      }}
                    >
                      <HighlightedJSON
                        data={selectedStep.error}
                        searchTerm={dataSearchTerm}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                  p: 2,
                  textAlign: "center"
                }}
              >
                <Typography>Select a step to view details</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

function DetailRow({
  label,
  value
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <Box mb={2}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function HighlightedJSON({
  data,
  searchTerm
}: {
  data: unknown
  searchTerm: string
}) {
  const jsonString = JSON.stringify(data || {}, null, 2)
  if (!searchTerm) {
    return <pre style={{ margin: 0 }}>{jsonString}</pre>
  }

  const parts = jsonString.split(new RegExp(`(${searchTerm})`, "gi"))
  return (
    <pre style={{ margin: 0 }}>
      {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <mark
            key={index}
            style={{
              backgroundColor: "#fef08a",
              color: "#000",
              borderRadius: "2px"
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </pre>
  )
}

function SdkFlowGroup({
  flowId,
  steps,
  onStepClick
}: {
  flowId: string
  steps: CallAdminLogStep[]
  onStepClick: (step: CallAdminLogStep) => void
}) {
  return (
    <Box
      sx={{
        border: "1px solid #cbd5e1",
        borderRadius: 1,
        mb: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper"
      }}
    >
      <Box sx={{ px: 1.5, py: 1, bgcolor: "#e2e8f0" }}>
        <Typography variant="body2" fontWeight={600}>
          Flow: {flowId}
        </Typography>
      </Box>

      {steps.map((step, index) => (
        <Box
          key={`${flowId}-${index}-${step.timestamp}`}
          onClick={() => onStepClick(step)}
          sx={{
            px: 1.5,
            py: 1,
            borderTop: index === 0 ? "none" : "1px solid #e2e8f0",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#f8fafc"
            }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" fontWeight={500}>
              {step.stepName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: "capitalize",
                color:
                  step.status === "failed" ? "error.main" : "text.secondary"
              }}
            >
              {step.status}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {step.type} - {new Date(step.timestamp).toLocaleTimeString()}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
